import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  makeStyles,
  Fade,
  Paper,
  ClickAwayListener,
  CircularProgress,
  Tooltip,
  Portal,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  reactionsWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  reactionsContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.type === "light" ? "#fff" : "#2a3942",
    borderRadius: "20px",
    padding: "0 6px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  },
  reactionButton: {
    fontSize: "1.1rem",
    cursor: "pointer",
    margin: "1px",
    padding: "3px",
    borderRadius: "50%",
    transition: "transform 0.2s ease, background-color 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    "&:hover": {
      backgroundColor: theme.palette.type === "light" ? "#f0f2f5" : "#384c56",
      transform: "scale(1.15)",
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  moreEmojisButton: {
    color: theme.palette.type === "light" ? "#54656f" : "#aebac1",
  },
  moreEmojisButtonActive: {
    backgroundColor: theme.palette.type === "light" ? "#f0f2f5" : "#384c56",
  },
  emojiPickerContainer: {
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
    maxWidth: "min(352px, calc(100vw - 24px))",
    "& .emoji-mart": {
      border: "none",
      width: "100% !important",
    },
  },
  selectedReaction: {
    backgroundColor: theme.palette.primary.light,
  },
  loading: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    borderRadius: "20px",
    zIndex: 10006,
  },
}));

const defaultEmojis = ["👍", "❤️", "🤣", "🤝", "😬", "🙏"];

const MessageReactions = ({ message, onClose, ticketId, channel, anchorEl }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMounted = useRef(true);
  const pickerRef = useRef(null);
  const [sendingReaction, setSendingReaction] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showFullPicker, setShowFullPicker] = useState(false);
  const maxRetries = 2;

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    let timeoutId;
    if (sendingReaction) {
      timeoutId = setTimeout(() => {
        if (isMounted.current) {
          setSendingReaction(false);
          setDisabled(false);
        }
      }, 20000);
    }
    return () => clearTimeout(timeoutId);
  }, [sendingReaction]);

  const sendReaction = useCallback(
    async (emoji) => {
      if (channel === "whatsapp_oficial") {
        toast.warn(i18n.t("messageOptionsMenu.officialApiNoReactions"), {
          toastId: "officialApiNoReactions",
          autoClose: 4000,
        });
        onClose();
        return false;
      }
      if (channel === "facebook" || channel === "instagram") {
        toast.warn(i18n.t("messageOptionsMenu.channelNoReactions"), {
          toastId: "channelNoReactions",
          autoClose: 4000,
        });
        onClose();
        return false;
      }

      try {
        if (isMounted.current) {
          setSendingReaction(true);
          setDisabled(true);
        }

        const shouldRemove = message.reaction === emoji;

        await api.post(`/messages/${ticketId}/reaction`, {
          messageId: message.id,
          reaction: shouldRemove ? "" : emoji,
        });

        if (isMounted.current) {
          setSendingReaction(false);
          setDisabled(false);
          setShowFullPicker(false);
          onClose();
        }

        return true;
      } catch (err) {
        console.error("Erro ao enviar reação:", err);

        const apiError =
          err.response?.data?.error || err.response?.data?.message || "";
        if (
          apiError === "ERR_OFFICIAL_NO_REACTIONS" ||
          apiError === "ERR_CHANNEL_NO_REACTIONS" ||
          apiError === "This channel doesn't support reactions"
        ) {
          toast.warn(
            i18n.t(
              apiError === "ERR_OFFICIAL_NO_REACTIONS" ||
                apiError === "This channel doesn't support reactions"
                ? "messageOptionsMenu.officialApiNoReactions"
                : "messageOptionsMenu.channelNoReactions"
            ),
            { toastId: apiError, autoClose: 4000 }
          );
          if (isMounted.current) {
            setSendingReaction(false);
            setDisabled(false);
            onClose();
          }
          return false;
        }

        if (isMounted.current) {
          if (retryCount < maxRetries) {
            setRetryCount((prev) => prev + 1);
          } else {
            toastError(err);
          }

          setSendingReaction(false);
          setDisabled(false);
        }

        return false;
      }
    },
    [
      message.id,
      ticketId,
      channel,
      onClose,
      retryCount,
      maxRetries,
      message.reaction,
    ]
  );

  const handleEmojiClick = async (emoji) => {
    if (sendingReaction || disabled || !isMounted.current) return;

    await sendReaction(emoji);
  };

  const handlePickerSelect = (emoji) => {
    if (!emoji?.native) return;
    handleEmojiClick(emoji.native);
  };

  const handleToggleFullPicker = (event) => {
    event.stopPropagation();
    if (disabled || sendingReaction) return;
    setShowFullPicker((prev) => !prev);
  };

  const handleClickAway = (event) => {
    if (pickerRef.current?.contains(event.target)) {
      return;
    }
    if (!sendingReaction) {
      onClose();
    }
  };

  const isCurrentReaction = (emoji) => {
    return message.reaction === emoji;
  };

  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorEl) {
      const updatePosition = () => {
        const rect = anchorEl.getBoundingClientRect();
        const pickerOffset = showFullPicker ? (isMobile ? 320 : 380) : 50;
        setPosition({
          top: rect.top - pickerOffset,
          left: rect.left + rect.width / 2,
        });
      };

      updatePosition();

      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [anchorEl, showFullPicker, isMobile]);

  if (!anchorEl) return null;

  const pickerTheme = theme.palette.type === "dark" ? "dark" : "light";

  return (
    <Portal>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div
          className={classes.reactionsWrapper}
          style={{
            position: "fixed",
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: "translateX(-50%)",
            zIndex: 10005,
            pointerEvents: "auto",
          }}
        >
          {showFullPicker && (
            <div
              ref={pickerRef}
              className={classes.emojiPickerContainer}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Picker
                perLine={isMobile ? 7 : 8}
                theme={pickerTheme}
                i18n={i18n}
                showPreview
                showSkinTones
                onSelect={handlePickerSelect}
              />
            </div>
          )}

          <Fade in>
            <Paper className={classes.reactionsContainer}>
              {sendingReaction && (
                <div className={classes.loading}>
                  <CircularProgress size={20} />
                </div>
              )}

              {defaultEmojis.map((emoji, index) => (
                <Tooltip
                  key={index}
                  title={isCurrentReaction(emoji) ? "Clique para remover" : ""}
                  placement="top"
                >
                  <div
                    className={`${classes.reactionButton} ${
                      isCurrentReaction(emoji) ? classes.selectedReaction : ""
                    }`}
                    onClick={() => handleEmojiClick(emoji)}
                    style={{
                      opacity: disabled ? 0.5 : 1,
                      cursor: disabled ? "not-allowed" : "pointer",
                    }}
                  >
                    {emoji}
                  </div>
                </Tooltip>
              ))}

              <Tooltip title="Mais emojis" placement="top">
                <div
                  className={`${classes.reactionButton} ${classes.moreEmojisButton} ${
                    showFullPicker ? classes.moreEmojisButtonActive : ""
                  }`}
                  onClick={handleToggleFullPicker}
                  style={{
                    opacity: disabled ? 0.5 : 1,
                    cursor: disabled ? "not-allowed" : "pointer",
                  }}
                  aria-label="Mais emojis"
                >
                  <AddIcon fontSize="small" />
                </div>
              </Tooltip>
            </Paper>
          </Fade>
        </div>
      </ClickAwayListener>
    </Portal>
  );
};

export default MessageReactions;
